import { GetCountriesResponse, LocationsService } from "lib/services";
import { useEffect, useState } from "react";
import { AddressFormFields } from "view/Page/CheckoutPage/useChangeAddressForm";

const locationsService = new LocationsService();

enum ADDRESS_FIELDS {
  COUNTRY = "country",
  COUNTRY_CODE = "countryCode",
  CITY_SUBDIVISION_1 = "citySubdivision1",
  CITY = "city",
  COUNTRY_ISO_3 = "countryISO3",
}
const EMPTY_STRING = "";

export const useLocations = (
  currentCountry: string,
  onInputChange: (field: keyof AddressFormFields, value: any, flag?: string) => void
) => {
  const [countries, setCountries] = useState<GetCountriesResponse[]>([]);
  const [phoneCodes, setPhoneCodes] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>();
  const [cities, setCities] = useState<string[]>();
  const [isStateDisabled, setIsStateDisabled] = useState(true);
  const [isCityDisabled, setIsCityDisabled] = useState(true);
  const [useTextInputStates, setUseTextInputStates] = useState<boolean>(false);
  const [useTextInputCities, setUseTextInputCities] = useState<boolean>(false);

  // Fetch countries and phone codes once on mount.
  useEffect(() => {
    (async () => {
      try {
        const [fetchedCountries, fetchedPhoneCodes] = await Promise.all([
          locationsService.getCountries(),
          locationsService.getPhoneCodes(),
        ]);
        setCountries(fetchedCountries);
        setPhoneCodes(fetchedPhoneCodes);
      } catch (error) {
        console.error("Failed to load initial locations data:", error);
      }
    })();
  }, []);

  useEffect(() => {
    if (states && states.length === 0) {
      setUseTextInputStates(true);
      setIsStateDisabled(false);
    } else {
      setUseTextInputStates(false);
      setIsStateDisabled(false);
    }
  }, [states]);

  useEffect(() => {
    // there are states and no cities or no states hence no cities and should use open text field
    if (useTextInputStates || (cities && cities.length === 0)) {
      setUseTextInputCities(true);
      setIsCityDisabled(false);
      // there are states and cities so it should use dropdown
    } else if (!useTextInputStates && cities && cities.length !== 0) {
      setUseTextInputCities(false);
      setIsCityDisabled(false);
      // should reset everything
    } else {
      setUseTextInputCities(false);
      setIsCityDisabled(true);
    }
  }, [useTextInputStates, cities, states]);

  // When the countries are loaded, check the current country value.
  // (Assumes that the initial country value is an ISO3 code and converts it to the country name.)
  useEffect(() => {
    if (countries.length === 0) return;
    if (currentCountry) {
      const matchedCountry = countries.find((c) => c.iso3 === currentCountry);
      // Reset dependent fields.
      onInputChange(ADDRESS_FIELDS.CITY_SUBDIVISION_1, EMPTY_STRING);
      onInputChange(ADDRESS_FIELDS.CITY, EMPTY_STRING);
      if (matchedCountry) {
        // Update the form value to the country name.
        handleCountryChange(matchedCountry.name);
      } else {
        onInputChange(ADDRESS_FIELDS.COUNTRY, EMPTY_STRING);
        onInputChange(ADDRESS_FIELDS.COUNTRY_ISO_3, EMPTY_STRING);
      }
    } else {
      onInputChange(ADDRESS_FIELDS.COUNTRY, EMPTY_STRING);
      onInputChange(ADDRESS_FIELDS.COUNTRY_ISO_3, EMPTY_STRING);
      onInputChange(ADDRESS_FIELDS.CITY_SUBDIVISION_1, EMPTY_STRING);
      onInputChange(ADDRESS_FIELDS.CITY, EMPTY_STRING);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries]);

  const handleCountryChange = async (selectedCountryName: string) => {
    onInputChange(ADDRESS_FIELDS.COUNTRY, selectedCountryName);
    const matchedCountry = countries.find((c) => c.name === selectedCountryName);
    if (!matchedCountry) {
      console.warn("No matching country found for name:", selectedCountryName);
      return;
    }
    // Update the country code in the form.
    onInputChange(ADDRESS_FIELDS.COUNTRY_CODE, matchedCountry.phonecode);
    onInputChange(ADDRESS_FIELDS.COUNTRY_ISO_3, matchedCountry.iso3);
    try {
      // Reset state and city fields.
      onInputChange(ADDRESS_FIELDS.CITY_SUBDIVISION_1, EMPTY_STRING);
      onInputChange(ADDRESS_FIELDS.CITY, EMPTY_STRING);
      const fetchedStates = await locationsService.getStates(matchedCountry.iso2);
      setStates(fetchedStates);
      setIsStateDisabled(false);
      setCities(undefined);
    } catch (error) {
      console.error("Failed to fetch states for iso2=", matchedCountry.iso2, error);
    }
  };

  const handleStateChange = async (selectedStateName: string) => {
    onInputChange(ADDRESS_FIELDS.CITY_SUBDIVISION_1, selectedStateName);
    onInputChange(ADDRESS_FIELDS.CITY, EMPTY_STRING);
    const countryName = currentCountry;
    const matchedCountry = countries.find((c) => c.name === countryName);
    if (!matchedCountry) {
      console.warn("Cannot fetch cities, no matched iso2 for country:", countryName);
      return;
    }
    try {
      const fetchedCities = await locationsService.getCities(matchedCountry.iso2, selectedStateName);
      setCities(fetchedCities);
    } catch (error) {
      console.error(`Failed to fetch cities for iso2=${matchedCountry.iso2}, state=${selectedStateName}`, error);
    }
  };

  const handleCityChange = (selectedCity: string) => {
    onInputChange(ADDRESS_FIELDS.CITY, selectedCity);
  };

  return {
    countries,
    phoneCodes,
    states,
    cities,
    isStateDisabled,
    isCityDisabled,
    useTextInputStates,
    useTextInputCities,
    handleCountryChange,
    handleStateChange,
    handleCityChange,
  };
};
